/**
 * מדיניות ערוצים — kill switches + per-type channel order / enabled.
 * Writes go through nivMessagingAdmin.setPolicy (validated server-side).
 * Kill switches are consequential → ConfirmDialog before applying.
 * Error codes: CK-10500 (load/init), CK-10501 (save).
 */
import { useMemo, useState } from "react";
import { WhatsappLogo, ChatText, ShieldCheck, Warning, ArrowsClockwise, Info } from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";
import HoverTip from "../../../../components/ui/HoverTip";
import { useToast } from "../../../../contexts/ToastContext";
import {
  useGetMessagingPolicyQuery,
  useSetMessagingPolicyMutation,
  useInitMessagingPolicyMutation,
  useGetWhatsAppStatusQuery,
} from "../../../../store/services/nivApi";
import {
  MESSAGE_TYPES,
  MESSAGE_TYPE_IDS,
  CHANNEL_LABELS,
  GROUP_LABELS,
  ORDER_OPTIONS,
  META_STATUS_LABELS,
  effectiveOrder,
  formatWhen,
} from "./messagesShared";

const SWITCHES = [
  { key: "whatsappEnabled", label: "וואטסאפ פעיל", off: "וואטסאפ כבוי — כל ההודעות יישלחו ב-SMS (ללא רישום שגיאות גיבוי)", icon: WhatsappLogo },
  { key: "smsEnabled", label: "SMS פעיל", off: "SMS כבוי — אין ערוץ גיבוי; כשל וואטסאפ = הודעה לא נשלחת (CK-9267)", icon: ChatText },
  { key: "fallbackEnabled", label: "גיבוי SMS פעיל", off: "גיבוי כבוי — מנסים רק את הערוץ הראשון", icon: ShieldCheck },
];

function OrderChips({ channels }) {
  if (!channels.length) return <span className="nmsg-chip nmsg-chip-muted">כבוי</span>;
  return (
    <span className="nmsg-order">
      {channels.map((c, i) => (
        <span key={c} className="nmsg-order-item">
          <span className={`nmsg-chip nmsg-chip-${c}`}>{CHANNEL_LABELS[c]}</span>
          {i < channels.length - 1 && <span className="nmsg-order-arrow">←</span>}
        </span>
      ))}
    </span>
  );
}

export default function PolicyPanel({ isAuthorized }) {
  const { showSuccess, showError } = useToast();
  const { data, isLoading, isFetching, refetch } = useGetMessagingPolicyQuery(undefined, { skip: !isAuthorized });
  const { data: waStatus } = useGetWhatsAppStatusQuery(undefined, { skip: !isAuthorized });
  const [setPolicy, { isLoading: saving }] = useSetMessagingPolicyMutation();
  const [initPolicy, { isLoading: initing }] = useInitMessagingPolicyMutation();
  const [confirmSwitch, setConfirmSwitch] = useState(null); // { key, next }
  const [groupFilter, setGroupFilter] = useState("all");

  const policy = data?.policy;
  const metaByTemplate = useMemo(() => {
    const m = {};
    (waStatus?.templates || []).forEach((t) => {
      m[t.id] = t;
    });
    return m;
  }, [waStatus]);

  const save = async (patch, okMsg = "המדיניות עודכנה") => {
    try {
      await setPolicy({ patch }).unwrap();
      showSuccess(okMsg);
    } catch (error) {
      console.error("[CK-10501] Messaging policy save failed:", {
        errorCode: "CK-10501",
        errorMessage: error?.message,
        patchKeys: Object.keys(patch || {}),
        timestamp: new Date().toISOString(),
      });
      showError(error?.message || "שמירת המדיניות נכשלה");
    }
  };

  const handleInit = async () => {
    try {
      await initPolicy().unwrap();
      showSuccess("מסמך המדיניות נוצר עם ברירות המחדל");
    } catch (error) {
      console.error("[CK-10500] Messaging policy init failed:", {
        errorCode: "CK-10500",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("יצירת המדיניות נכשלה");
    }
  };

  const applySwitch = async () => {
    if (!confirmSwitch) return;
    const { key, next } = confirmSwitch;
    setConfirmSwitch(null);
    await save({ [key]: next }, next ? "הערוץ הופעל" : "הערוץ כובה");
  };

  const onOrderChange = (typeId, value) => {
    if (value === "default") return save({ types: { [typeId]: { channels: null, enabled: policy?.types?.[typeId]?.enabled } } });
    return save({ types: { [typeId]: { channels: value.split(","), enabled: policy?.types?.[typeId]?.enabled } } });
  };

  // Phase 2 rollout switch: the legacy send site calls the router only when on.
  const onRoutedChange = (typeId, routed) =>
    save({ routed: { [typeId]: routed } }, routed ? "הסוג עבר לראוטר (וואטסאפ ← SMS)" : "הסוג חזר לנתיב ה-SMS הישן");

  const onEnabledChange = (typeId, enabled) =>
    save({ types: { [typeId]: { enabled, channels: policy?.types?.[typeId]?.channels || null } } }, enabled ? "סוג ההודעה הופעל" : "סוג ההודעה כובה");

  const rows = useMemo(
    () => MESSAGE_TYPE_IDS.filter((id) => groupFilter === "all" || MESSAGE_TYPES[id].channelGroup === groupFilter),
    [groupFilter]
  );

  if (isLoading) {
    return (
      <ContentLoader size="sm" compact message="טוען מדיניות…" />
    );
  }

  return (
    <div className="nmsg-panel">
      {data && !data.exists && (
        <div className="nmsg-notice nmsg-notice-warn">
          <Warning size={16} weight="fill" />
          <span>מסמך המדיניות (niv_settings/messaging) עדיין לא קיים — הראוטר עובד לפי ברירות המחדל.</span>
          <button type="button" className="niv-btn niv-btn-primary niv-btn-sm" onClick={handleInit} disabled={initing}>
            {initing ? <LoaderLoop size="xs" /> : <ShieldCheck size={14} />} צור מסמך מדיניות
          </button>
        </div>
      )}
      {data?.badTemplates?.length > 0 && (
        <div className="nmsg-notice nmsg-notice-bad">
          <Warning size={16} weight="fill" />
          <span>
            תבניות במצב בעייתי ב-Meta: {data.badTemplates.map((t) => `${t.name} (${t.status})`).join(", ")} — הסוגים שלהן נשלחים ב-SMS.
          </span>
        </div>
      )}

      {/* ---- global switches ---- */}
      <section className="nmsg-card">
        <header className="nmsg-card-head">
          <div className="nmsg-card-titles">
            <span className="nmsg-eyebrow">גלובלי</span>
            <h4 className="nmsg-card-title">מתגי ערוצים</h4>
          </div>
          <div className="nmsg-card-side">
            <span className="nmsg-muted">
              סדר ברירת מחדל: <OrderChips channels={["whatsapp", "sms"]} />
            </span>
            <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={() => refetch()} disabled={isFetching}>
              <ArrowsClockwise size={14} className={isFetching ? "spinning" : ""} /> רענון
            </button>
          </div>
        </header>
        <div className="nmsg-switches">
          {SWITCHES.map((s) => {
            const on = policy ? policy[s.key] !== false : true;
            return (
              <label key={s.key} className={`nmsg-switch ${on ? "on" : "off"}`}>
                <span className="nmsg-switch-icon">
                  <s.icon size={16} weight="fill" />
                </span>
                <span className="nmsg-switch-text">
                  <strong>{s.label}</strong>
                  <small>{on ? "פעיל" : s.off}</small>
                </span>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={saving || !data?.exists}
                  onChange={(e) => setConfirmSwitch({ key: s.key, next: e.target.checked, label: s.label, off: s.off })}
                  aria-label={s.label}
                />
                <span className="nmsg-toggle" aria-hidden="true" />
              </label>
            );
          })}
        </div>
        {data?.updatedAt && (
          <p className="nmsg-muted nmsg-updated">
            עודכן {formatWhen(data.updatedAt)} · {data.updatedBy || "—"}
          </p>
        )}
      </section>

      {/* ---- per type ---- */}
      <section className="nmsg-card">
        <header className="nmsg-card-head">
          <div className="nmsg-card-titles">
            <span className="nmsg-eyebrow">{rows.length} סוגי הודעה</span>
            <h4 className="nmsg-card-title">סדר ערוצים לפי סוג</h4>
          </div>
          <div className="nmsg-filters" role="group" aria-label="קבוצה">
            {[
              ["all", "הכל"],
              ["patients", GROUP_LABELS.patients],
              ["users", GROUP_LABELS.users],
            ].map(([v, l]) => (
              <button
                key={v}
                type="button"
                className={`nmsg-filter ${groupFilter === v ? "active" : ""}`}
                onClick={() => setGroupFilter(v)}
              >
                {l}
              </button>
            ))}
          </div>
        </header>
        <p className="nmsg-muted nmsg-help">
          <Info size={13} /> <strong>דרך הראוטר</strong> = נתיב השליחה החדש (וואטסאפ ← SMS). כבוי = נתיב ה-SMS הישן, ללא שינוי. להפעיל רק אחרי שהתבנית מאושרת ב-Meta — אחרת כל הודעה תירשם כ-CK-9266. "SMS בלבד" / "SMS ← וואטסאפ" הם בחירה מכוונת — בלי רישום שגיאת גיבוי.
        </p>

        <div className="nmsg-table" role="table">
          <div className="nmsg-tr nmsg-th" role="row">
            <span>סוג הודעה</span>
            <span>קבוצה</span>
            <span>תבנית Meta</span>
            <span>סדר בפועל</span>
            <span>בחירה</span>
            <span>פעיל</span>
            <span>דרך הראוטר</span>
          </div>
          {rows.map((id) => {
            const t = MESSAGE_TYPES[id];
            const ov = policy?.types?.[id] || {};
            const eff = effectiveOrder(policy, id);
            const meta = t.waTemplateId ? metaByTemplate[t.waTemplateId] : null;
            const ms = meta ? META_STATUS_LABELS[meta.metaStatus] || { label: meta.metaStatus, tone: "warn" } : null;
            const selectValue = ov.channels?.length ? ov.channels.join(",") : "default";
            const enabled = ov.enabled === undefined ? t.defaultEnabled !== false : ov.enabled !== false;
            const routed = policy?.routed?.[id] === true;
            const approved = meta?.metaStatus === "APPROVED";
            return (
              <div key={id} className={`nmsg-tr ${enabled ? "" : "is-off"}`} role="row">
                <span className="nmsg-td-name">
                  <strong>{t.label}</strong>
                  <small dir="ltr">
                    {id} · phase {t.phase}
                  </small>
                </span>
                <span>
                  <span className={`nmsg-chip nmsg-chip-group-${t.channelGroup}`}>{GROUP_LABELS[t.channelGroup]}</span>
                </span>
                <span>
                  {t.waTemplateId ? (
                    <HoverTip text={meta ? `${meta.name} · ${meta.metaStatus}${meta.rejectedReason ? ` · ${meta.rejectedReason}` : ""}` : t.waTemplateId}>
                      <span className={`nmsg-chip nmsg-chip-${ms ? ms.tone : "muted"}`}>{ms ? ms.label : "טוען…"}</span>
                    </HoverTip>
                  ) : (
                    <span className="nmsg-chip nmsg-chip-muted">אין תבנית</span>
                  )}
                </span>
                <span>
                  <OrderChips channels={eff.channels} />
                  {eff.source === "override" && <span className="nmsg-chip nmsg-chip-override">override</span>}
                </span>
                <span>
                  <select
                    className="nmsg-select"
                    value={selectValue}
                    disabled={saving || !data?.exists}
                    onChange={(e) => onOrderChange(id, e.target.value)}
                    aria-label={`סדר ערוצים — ${t.label}`}
                  >
                    {ORDER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.value === "default" ? `${o.label} (${t.channels.map((c) => CHANNEL_LABELS[c]).join(" ← ")})` : o.label}
                      </option>
                    ))}
                  </select>
                </span>
                <span>
                  <label className="nmsg-mini-toggle">
                    <input
                      type="checkbox"
                      checked={enabled}
                      disabled={saving || !data?.exists}
                      onChange={(e) => onEnabledChange(id, e.target.checked)}
                      aria-label={`פעיל — ${t.label}`}
                    />
                    <span className="nmsg-toggle" aria-hidden="true" />
                  </label>
                </span>
                <span>
                  <HoverTip
                    text={
                      routed
                        ? "השליחה עוברת דרך הראוטר (וואטסאפ ← SMS)"
                        : t.phase >= 4
                        ? "אוטומציה חדשה — אין עדיין קוד שליחה"
                        : approved
                        ? "נתיב SMS ישן. התבנית מאושרת — אפשר להעביר"
                        : "נתיב SMS ישן. התבנית עדיין לא מאושרת ב-Meta"
                    }
                  >
                    <label className={`nmsg-mini-toggle ${!routed && approved ? "is-ready" : ""}`}>
                      <input
                        type="checkbox"
                        checked={routed}
                        disabled={saving || !data?.exists}
                        onChange={(e) => onRoutedChange(id, e.target.checked)}
                        aria-label={`דרך הראוטר — ${t.label}`}
                      />
                      <span className="nmsg-toggle" aria-hidden="true" />
                    </label>
                  </HoverTip>
                  {routed && <span className="nmsg-chip nmsg-chip-ok">פעיל</span>}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <ConfirmDialog
        isOpen={!!confirmSwitch}
        onClose={() => setConfirmSwitch(null)}
        onConfirm={applySwitch}
        title={confirmSwitch?.next ? `להפעיל "${confirmSwitch?.label}"?` : `לכבות "${confirmSwitch?.label}"?`}
        message={confirmSwitch?.next ? "השינוי חל מיידית על כל הקליניקות." : `${confirmSwitch?.off || ""} השינוי חל מיידית על כל הקליניקות.`}
        confirmText={confirmSwitch?.next ? "הפעל" : "כבה"}
        type={confirmSwitch?.next ? "info" : "danger"}
      />
    </div>
  );
}
